<template>
  <v-container fluid class="pa-0 fill-height">
    <v-card class="fill-height d-flex flex-column" style="width: 100%">
      <v-toolbar color="primary" density="compact">
        <v-spacer />
        <v-btn icon @click="prevWeek">
          <v-icon size="large">mdi-chevron-left</v-icon>
        </v-btn>
        <span class="text-h6 mx-2">{{ currentWeekLabel }}</span>
        <v-btn icon @click="nextWeek">
          <v-icon size="large">mdi-chevron-right</v-icon>
        </v-btn>
        <v-spacer />
        <v-btn icon @click="goToToday" variant="flat" color="primary">
          <v-icon>mdi-calendar-today</v-icon>
          <v-tooltip activator="parent" location="bottom">Today</v-tooltip>
        </v-btn>
        <!-- Export PDF button -->
        <v-btn icon @click="exportPdf" variant="flat" color="primary">
          <v-icon>mdi-file-pdf-box</v-icon>
          <v-tooltip activator="parent" location="bottom">Export PDF</v-tooltip>
        </v-btn>
        <!-- Close button — closes the calendar tab/window or navigates to reviews-home -->
        <v-btn icon @click="closeWindow" variant="flat" color="primary">
          <v-icon>mdi-close</v-icon>
          <v-tooltip activator="parent" location="bottom">Close</v-tooltip>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 flex-grow-1">
        <v-calendar
          :key="calendarKey"
          ref="calendarRef"
          v-model="calendarDate"
          type="custom-weekly"
          :start="weekStart"
          :end="weekEnd"
          :events="calendarEvents"
          event-start="start"
          event-end="end"
          :first-day-of-week="1"
          :event-color="getEventColor"
          event-overlap-mode="column"
        >
          <template v-slot:day="{ date, past, weekday }">
            <div
              class="day-click-target"
              :class="{ 'past-day': past, 'weekend-day': weekday === 0 || weekday === 6 }"
              @click="onDayClick(date)"
            />
            <!-- Sticky daily cost at bottom of day cell -->
            <div v-if="getDailyCost(date) > 0" class="day-cost-badge">
              {{ formatCost(getDailyCost(date)) }}
            </div>
          </template>
          <template v-slot:event="{ event }">
            <div
              class="calendar-event-chip"
              @click.stop="onEventAction('info', event)"
            >
              <div :style="{ background: event.gradient || event.color, width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0 }"></div>
              <v-icon
                v-if="event.statusIcon"
                :icon="event.statusIcon"
                size="x-small"
              />
              <span class="text-caption text-truncate">{{ event.title }}</span>
              <v-tooltip activator="parent" location="top">{{ event.entry.platform }} · {{ event.entry.location }} · {{ event.entry.brand }} — {{ event.userName }}</v-tooltip>
            </div>
          </template>
        </v-calendar>
      </v-card-text>

      <v-card-actions class="border-t">
        <span class="text-caption text-medium-emphasis">Click on a day to add an assignment</span>
        <!-- Show active filters from assignments page -->
        <v-chip v-if="assignmentFilters.platform" size="x-small" variant="outlined" color="primary" class="ml-2">
          {{ assignmentFilters.platform }}
        </v-chip>
        <v-chip v-for="loc in (assignmentFilters.location || [])" :key="'loc-' + loc" size="x-small" variant="outlined" color="primary" class="ml-2">
          {{ loc }}
        </v-chip>
        <v-chip v-for="br in (assignmentFilters.brand || [])" :key="'br-' + br" size="x-small" variant="outlined" color="primary" class="ml-2">
          {{ br }}
        </v-chip>
        <v-chip v-if="assignmentFilters.user_id" size="x-small" variant="outlined" color="primary" class="ml-2">
          {{ getUserNameFromMap(assignmentFilters.user_id) }}
        </v-chip>
        <v-spacer />
        <v-chip v-if="periodCost > 0" size="small" variant="tonal" color="primary" class="mr-2">
          {{ formatCost(periodCost) }} AED
        </v-chip>
        <span class="text-caption text-grey">
          {{ periodEventCount }} assignment{{ periodEventCount !== 1 ? "s" : "" }}
        </span>
      </v-card-actions>
    </v-card>

    <!-- Edit Assignment Dialog (shared component) -->
    <ReviewScheduleEditDialog
      v-model="editDialogOpen"
      :mode="editDialogMode"
      :entry="editDialogEntry"
      @saved="refreshCalendar"
    />

    <!-- Info Dialog -->
    <v-dialog v-model="infoDialogOpen" max-width="400">
      <v-card v-if="selectedEvent" class="d-flex flex-column" style="max-height: 80vh">
        <v-toolbar density="compact" color="primary">
          <v-toolbar-title class="text-subtitle-1">
            {{ selectedEvent.entry?.location }} · {{ selectedEvent.entry?.brand }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon size="small" @click="infoDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="flex-grow-1 overflow-y-auto">
          <div class="text-body-2 text-medium-emphasis mb-3">
            {{ formatDate(selectedEvent.start) }}
          </div>
          <div class="d-flex align-center ga-2 mb-3">
            <div :style="{ background: getPlatformGradient(selectedEvent.entry?.platform), width: '22px', height: '22px', borderRadius: '3px', flexShrink: 0 }"></div>
            <span class="text-body-2">
              <strong>{{ selectedEvent.entry?.platform }}</strong> · {{ selectedEvent.entry?.location }} · {{ selectedEvent.entry?.brand }}
            </span>
          </div>
          <v-list density="compact" class="pa-0">
            <v-list-item class="px-0">
              <template v-slot:prepend>
                <v-icon size="small" class="mr-2">mdi-account</v-icon>
              </template>
              <v-list-item-title class="text-body-2">{{
                selectedEvent.userName
              }}</v-list-item-title>
              <v-list-item-subtitle>Assigned user</v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="px-0 mb-2">
              <template v-slot:prepend>
                <v-icon size="small" class="mr-2">mdi-clipboard-check-outline</v-icon>
              </template>
              <v-list-item-title class="text-body-2">
                {{ selectedEvent.entry?.completion_count || 0 }} /
                {{ selectedEvent.entry?.num_reviews }}
              </v-list-item-title>
              <v-list-item-subtitle>Completed</v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="px-0 mb-2">
              <template v-slot:prepend>
                <v-icon size="small" class="mr-2">mdi-flag</v-icon>
              </template>
              <v-list-item-title>
                <v-chip size="small" :color="getStatusColor(selectedEvent.status)" variant="flat">
                  {{ selectedEvent.status }}
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle>Status</v-list-item-subtitle>
            </v-list-item>
          </v-list>

          <!-- Completions -->
          <div v-if="infoCompletions.length > 0" class="mt-3">
            <div class="text-caption text-medium-emphasis mb-1">Uploaded screenshots</div>
            <div
              v-for="c in infoCompletions"
              :key="c.id"
              class="d-flex align-center ga-2 mb-1"
            >
              <v-icon icon="mdi-check-circle" color="success" size="small" />
              <span class="text-body-2 flex-grow-1">
                {{
                  c.review_text
                    ? c.review_text.substring(0, 60) + (c.review_text.length > 60 ? "..." : "")
                    : "Screenshot uploaded"
                }}
              </span>
              <v-btn
                v-if="c.asset_id"
                icon
                size="x-small"
                variant="text"
                @click="viewScreenshot(c.asset_id)"
              >
                <v-icon size="small">mdi-image</v-icon>
                <v-tooltip activator="parent" location="top">View screenshot</v-tooltip>
              </v-btn>
            </div>
          </div>
          <v-progress-linear v-else-if="infoCompletionsLoading" indeterminate class="mt-3" />
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn variant="outlined" @click="infoDialogOpen = false">Close</v-btn>
          <v-spacer />
          <v-btn icon size="small" variant="text" @click="infoToEdit">
            <v-icon>mdi-pencil</v-icon>
            <v-tooltip activator="parent" location="top">Edit</v-tooltip>
          </v-btn>
          <v-btn icon size="small" variant="text" @click="infoToDuplicate">
            <v-icon>mdi-content-duplicate</v-icon>
            <v-tooltip activator="parent" location="top">Duplicate</v-tooltip>
          </v-btn>
          <v-btn icon size="small" variant="text" color="error" @click="infoToDelete">
            <v-icon>mdi-delete</v-icon>
            <v-tooltip activator="parent" location="top">Delete</v-tooltip>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Screenshot viewer -->
    <v-dialog v-model="screenshotDialog" max-width="600">
      <v-card>
        <v-toolbar color="primary" density="compact">
          <v-toolbar-title>Screenshot</v-toolbar-title>
          <v-spacer />
          <v-btn icon size="small" @click="screenshotDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="text-center pa-2">
          <v-progress-circular v-if="screenshotLoading" indeterminate class="my-8" />
          <v-img v-else-if="screenshotUrl" :src="screenshotUrl" max-height="80vh" contain />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Review Settings Dialog (cooldown + cost per platform) -->
    <ReviewSettingsDialog v-model="costDialogOpen" @saved="onCostsSaved" />

    <!-- Add/Duplicate Assignment Dialog (shared component) -->
    <ReviewScheduleEditDialog
      v-model="addDialogOpen"
      :mode="addDialogMode"
      :entry="addDialogEntry"
      :prefill="addDialogPrefill"
      @saved="refreshCalendar"
    />

    <!-- Shared filter dialog — reads/writes the same filters ref via useReviewAssignments -->
    <ReviewAssignmentFilterDialog v-model="showFilters" />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script setup>
import { ref, computed, watch, watchEffect, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import api from "@/utils/api.js";
import { useReviewSchedule } from "@/composables/useReviewSchedule.js";
import { useReviewAssignments } from "@/composables/useReviewAssignments.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";
import { usePlatformColors } from "@/composables/usePlatformColors.js";
import ReviewScheduleEditDialog from "@/components/ReviewScheduleEditDialog.vue";
import ReviewSettingsDialog from "@/components/ReviewSettingsDialog.vue";
import ReviewAssignmentFilterDialog from "@/components/ReviewAssignmentFilterDialog.vue";

const router = useRouter();
const { mobile } = useDisplay();

// Schedule composable: schedule entries, loading state, selected month for fetching,
// snackbar, and schedule CRUD/export helpers.
const {
  scheduleEntries,    // All schedule entries for the current month (fetched from backend)
  scheduleLoading,    // True while fetching schedule
  selectedMonth,      // Current month string (YYYY-MM) used by fetchSchedule
  snackbar,           // Shared snackbar state { show, message, color }
  fetchSchedule,      // Fetches schedule entries for selectedMonth from the backend
  deleteEntry,        // Deletes a schedule entry by ID; shows snackbar on error
  exportCalendarPdf,  // Writable ref: set to exportPdf so parent components can trigger PDF export
} = useReviewSchedule();

// Shared filters from the assignments management page (ReviewAssignments.vue).
// This calendar reads these filters reactively — the user sets them on the
// assignments page and they persist here via the singleton composable.
// Filters: platform (string), location (array), brand (array), user_id (number), dateFrom/dateTo (strings).
const { filters: assignmentFilters } = useReviewAssignments();

// --- Filter dialog state ---

// Controls visibility of the shared filter dialog component
const showFilters = ref(false);

// Count of active filters — shown as a badge on the filter icon in the app bar
const activeFilterCount = computed(() => {
  let count = 0;
  if (assignmentFilters.value.dateFrom || assignmentFilters.value.dateTo) count++;
  if (assignmentFilters.value.location?.length) count++;
  if (assignmentFilters.value.platform) count++;
  if (assignmentFilters.value.brand?.length) count++;
  if (assignmentFilters.value.user_id) count++;
  return count;
});

// App bar actions API: lets this page register buttons in the global app bar.
const { setActions, clearActions } = useAppBarActions();

// --- Calendar refs ---

// Direct reference to the Vuetify v-calendar component (not currently used, kept for future API access).
const calendarRef = ref(null);

// The currently displayed date; drives weekStart/weekEnd computeds for the two-week view.
const calendarDate = ref(new Date());

// Incremented to force the v-calendar to fully re-render after data changes (key trick).
const calendarKey = ref(0);

// True while a PDF export is in progress (used to disable the export button).
const exporting = ref(false);

// Map of userId → { name, email } for resolving assigned user names on event chips and dialogs.
// Populated by loadSupportData() on mount.
const usersMap = ref(new Map());

// --- Info dialog refs ---

// The calendar event object currently selected (set when user clicks an event chip).
const selectedEvent = ref(null);

// Controls visibility of the info dialog (shows assignment details for a clicked event).
const infoDialogOpen = ref(false);

// List of completion records (uploaded screenshots) for the currently selected schedule entry.
// Fetched from the backend when the info dialog opens and completion_count > 0.
const infoCompletions = ref([]);

// True while fetching completion records for the info dialog.
const infoCompletionsLoading = ref(false);

// --- Screenshot viewer refs ---

// Controls visibility of the screenshot viewer dialog.
const screenshotDialog = ref(false);

// Signed URL for the screenshot image, fetched from the assets API on demand.
const screenshotUrl = ref(null);

// True while fetching the signed screenshot URL from the assets API.
const screenshotLoading = ref(false);

// --- Edit dialog refs (shared ReviewScheduleEditDialog instance for editing) ---

// Controls visibility of the edit dialog (for editing an existing schedule entry).
const editDialogOpen = ref(false);

// Mode passed to ReviewScheduleEditDialog — always "edit" for this dialog instance.
const editDialogMode = ref("edit");

// The schedule entry object being edited; passed to the dialog as a prop.
const editDialogEntry = ref(null);

// --- Add/duplicate dialog refs (shared ReviewScheduleEditDialog instance for add/duplicate) ---

// Controls visibility of the add/duplicate dialog.
const addDialogOpen = ref(false);

// Mode passed to ReviewScheduleEditDialog: "add" when creating a new entry,
// "duplicate" when cloning an existing one.
const addDialogMode = ref("add");

// The source entry for duplication (null when mode is "add").
const addDialogEntry = ref(null);

// Pre-fill values for a new entry (e.g., { startDate, endDate } when user clicks a calendar day).
// Null when duplicating (the entry itself provides the defaults).
const addDialogPrefill = ref(null);

// ── Review cost per platform ─────────────────────────────────────────────────

// Config data from backend: key/value pairs including cost_per_review:<Platform>
const reviewConfig = ref({});

// Controls visibility of the ReviewSettingsDialog
const costDialogOpen = ref(false);

/**
 * Compute the cost for a given day based on the number of assignments per platform
 * and the configured cost_per_review for each platform.
 */
function getDailyCost(dateStr) {
  const d = typeof dateStr === "string" ? dateStr.slice(0, 10) : "";
  let total = 0;
  for (const event of calendarEvents.value) {
    if (event.start === d) {
      const platform = event.entry?.platform;
      const costKey = `cost_per_review:${platform}`;
      const cost = parseFloat(reviewConfig.value[costKey]) || 0;
      total += cost;
    }
  }
  return total;
}

/** Total cost for events within the current two-week view interval. */
const periodCost = computed(() => {
  let total = 0;
  for (const event of calendarEvents.value) {
    if (event.start >= weekStart.value && event.start <= weekEnd.value) {
      const costKey = `cost_per_review:${event.entry?.platform}`;
      total += parseFloat(reviewConfig.value[costKey]) || 0;
    }
  }
  return total;
});

/** Count of events within the current two-week view interval. */
const periodEventCount = computed(() => {
  return calendarEvents.value.filter(e => e.start >= weekStart.value && e.start <= weekEnd.value).length;
});

/** Format a number as currency with 2 decimal places. */
function formatCost(value) {
  return value.toFixed(2);
}

/** Reload config after settings are saved so daily costs update immediately. */
async function onCostsSaved() {
  snackbar.value = { show: true, message: "Review settings saved", color: "success" };
  await loadReviewConfig();
}

/** Fetch review config from backend (called on mount). */
async function loadReviewConfig() {
  try {
    const res = await api.marketingApi.get("/api/marketing/reviews/admin/config");
    reviewConfig.value = res.data || {};
  } catch (err) {
    console.warn("Failed to load review config:", err.message);
  }
}

// --- Week navigation refs ---

// ISO date string (YYYY-MM-DD) of the Monday that was last fetched, used to avoid
// duplicate fetches when calendarDate changes within the same week.
const lastFetchedWeek = ref(null);

/**
 * Called when the user clicks on a calendar day cell.
 * Ignores past dates. For future/today dates, opens the add dialog
 * with the clicked date pre-filled as both start and end date.
 */
function onDayClick(date) {
  const today = new Date().toISOString().slice(0, 10);
  if (date < today) return; // Past days are not clickable

  // Open the add dialog with the clicked date pre-filled
  addDialogMode.value = "add";
  addDialogEntry.value = null;
  addDialogPrefill.value = { startDate: date, endDate: date };
  addDialogOpen.value = true;
}

/**
 * Resolves a user display name from the usersMap by userId.
 * Falls back to "User #<id>" if the user is not found in the map
 * (e.g., the user was deleted or loadSupportData hasn't completed yet).
 * Used in the footer filter chip and info dialog.
 */
function getUserNameFromMap(userId) {
  const u = usersMap.value.get(userId);
  return u?.name || `User #${userId}`;
}

/**
 * Computes the ISO date string (YYYY-MM-DD) of the Monday that starts
 * the two-week window shown in the calendar. The calendar uses a
 * custom-weekly type spanning weekStart → weekEnd (14 days total).
 */
const weekStart = computed(() => {
  const date = new Date(calendarDate.value);
  const day = date.getDay();
  const monday = new Date(date);
  // (day + 6) % 7 converts Sunday=0…Saturday=6 into Monday=0…Sunday=6
  monday.setDate(date.getDate() - ((day + 6) % 7));
  return monday.toISOString().slice(0, 10);
});

/**
 * Computes the ISO date string (YYYY-MM-DD) of the Sunday that ends
 * the two-week window (Monday + 13 days = the following Sunday).
 */
const weekEnd = computed(() => {
  const date = new Date(calendarDate.value);
  const day = date.getDay();
  const monday = new Date(date);
  monday.setDate(date.getDate() - ((day + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 13); // 14-day window ending on Sunday
  return sunday.toISOString().slice(0, 10);
});

/**
 * Computes the human-readable label shown in the toolbar, e.g.:
 * "Jan 6 – Jan 19, 2025"
 * Displays the full two-week range from Monday to the following Sunday.
 */
const currentWeekLabel = computed(() => {
  const date = new Date(calendarDate.value);
  // Find Monday of the current week
  const day = date.getDay();
  const monday = new Date(date);
  monday.setDate(date.getDate() - ((day + 6) % 7));
  // End of the two-week window (Sunday, 13 days after Monday)
  const endSunday = new Date(monday);
  endSunday.setDate(monday.getDate() + 13);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const year = monday.getFullYear();
  return `${fmt(monday)} – ${fmt(endSunday)}, ${year}`;
});

/** Returns a Vuetify color name for a schedule entry status chip. */
const getStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "success";
    case "partial":
      return "warning";
    case "skipped":
      return "error";
    default:
      return "grey";
  }
};

/** Returns an MDI icon name for a schedule entry status badge on event chips. */
const getStatusIcon = (status) => {
  switch (status) {
    case "completed":
      return "mdi-check-circle";
    case "partial":
      return "mdi-circle-half-full";
    case "skipped":
      return "mdi-close-circle";
    default:
      return "mdi-circle-outline";
  }
};

const { getPlatformColor, getPlatformGradient, getPlatformTextColor } = usePlatformColors();

/**
 * Converts scheduleEntries into the event objects expected by v-calendar,
 * applying all active filters from the shared assignmentFilters state.
 *
 * Filters applied (all optional; a missing/empty filter means "show all"):
 *   - dateFrom / dateTo: ISO date strings for a date range (string comparison on YYYY-MM-DD)
 *   - platform: exact string match (e.g. "Talabat")
 *   - location: array of location name strings — entry must be in the array
 *   - brand: array of brand name strings — entry must be in the array
 *   - user_id: number — only entries assigned to this user ID
 *
 * Each event object includes the raw schedule entry (as `entry`) so info/edit
 * dialogs can access all fields without additional lookups.
 */
const calendarEvents = computed(() => {
  const f = assignmentFilters.value;
  return scheduleEntries.value.filter((entry) => {
    // Normalize schedule_date to a YYYY-MM-DD string for comparison
    const d = typeof entry.schedule_date === "string" ? entry.schedule_date.slice(0, 10) : "";
    if (f.dateFrom && d < f.dateFrom) return false;
    if (f.dateTo && d > f.dateTo) return false;
    // Platform is a single string filter (exact match)
    if (f.platform && entry.platform !== f.platform) return false;
    // Location and brand are multi-select arrays; empty array means no filter
    if (f.location?.length && !f.location.includes(entry.location)) return false;
    if (f.brand?.length && !f.brand.includes(entry.brand)) return false;
    // User filter: only show entries assigned to the selected user
    if (f.user_id && entry.assigned_user_id !== f.user_id) return false;
    return true;
  }).map((entry) => {
    // Normalize schedule_date: handle both Date objects and ISO strings
    const dateStr =
      entry.schedule_date instanceof Date
        ? entry.schedule_date.toISOString().slice(0, 10)
        : String(entry.schedule_date).slice(0, 10);
    const user = usersMap.value.get(entry.assigned_user_id);
    const userName = user?.name || `User #${entry.assigned_user_id}`;

    return {
      title: `${entry.location} - ${entry.brand}`,
      start: dateStr,
      end: dateStr,
      color: getPlatformColor(entry.platform),       // Solid color fallback
      gradient: getPlatformGradient(entry.platform), // CSS gradient for the color swatch
      textColor: getPlatformTextColor(entry.platform),
      status: entry.status,
      statusIcon: getStatusIcon(entry.status), // Icon shown inside the event chip
      userName,
      entry, // Raw DB entry, passed through to dialogs
    };
  });
});

/**
 * Required by v-calendar's event-color prop.
 * Returns a fixed fallback color; actual event colors are applied via the
 * custom event slot (calendar-event-chip), not through this callback.
 */
function getEventColor() {
  return "grey-darken-3";
}

/**
 * Formats an ISO date string for display in the info dialog header.
 * Appends T00:00:00 to force local-timezone interpretation (avoids off-by-one-day issues).
 */
function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// --- Week navigation ---

/** Moves the calendar back by one week (7 days). */
function prevWeek() {
  const date = new Date(calendarDate.value);
  date.setDate(date.getDate() - 7);
  calendarDate.value = date;
}

/** Moves the calendar forward by one week (7 days). */
function nextWeek() {
  const date = new Date(calendarDate.value);
  date.setDate(date.getDate() + 7);
  calendarDate.value = date;
}

/** Resets the calendar to today's date. */
function goToToday() {
  calendarDate.value = new Date();
}

/** Close the calendar tab/window. Falls back to navigating to reviews-home
 *  if the window can't be closed (e.g. not opened by JS, or last tab). */
function closeWindow() {
  window.close();
  // window.close() is a no-op if the tab wasn't opened by script —
  // navigate away so the URL doesn't stick on /reviews-calendar
  setTimeout(() => router.push("/reviews"), 100);
}

// Watch calendarDate and re-fetch schedule when the displayed week changes.
// Uses lastFetchedWeek to deduplicate fetches — multiple date changes within
// the same week (e.g., goToToday() when already in the current week) are ignored.
// Also updates selectedMonth when the week crosses a month boundary so that
// fetchSchedule queries the correct month from the backend.
watch(calendarDate, (newDate) => {
  const d = new Date(newDate);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7));
  const weekKey = monday.toISOString().slice(0, 10); // Unique key per Monday
  if (weekKey === lastFetchedWeek.value) return; // Already fetched for this week
  lastFetchedWeek.value = weekKey;

  // Update selectedMonth if we've crossed into a new month
  const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  if (month !== selectedMonth.value) {
    selectedMonth.value = month;
  }
  fetchSchedule();
});

/**
 * Handles all calendar event actions dispatched from the event chip or info dialog.
 *
 * Actions:
 *   "info"      — Opens the info dialog and fetches completion records if any exist.
 *                 Completions are loaded lazily only when completion_count > 0.
 *   "edit"      — Opens the shared edit dialog (editDialogOpen) with the entry.
 *   "duplicate" — Opens the shared add/duplicate dialog (addDialogOpen) in "duplicate" mode.
 *   "delete"    — Deletes the entry via the composable; refreshes the calendar on success.
 *                 Errors are surfaced via the composable's snackbar, not thrown here.
 */
async function onEventAction(action, event) {
  selectedEvent.value = event;
  const entry = event.entry;

  if (action === "info") {
    infoDialogOpen.value = true;
    infoCompletions.value = []; // Reset previous completions
    // Fetch completion records only when there is at least one (avoids unnecessary API calls)
    if (entry?.completion_count > 0) {
      infoCompletionsLoading.value = true;
      try {
        // Completions are now embedded in the assignment row — no separate API call needed
        infoCompletions.value = entry.asset_id ? [{ asset_id: entry.asset_id, completed_at: entry.completed_at }] : [];
      } catch {
        // Silently fail — completions are supplementary info, not critical
      } finally {
        infoCompletionsLoading.value = false;
      }
    }
  } else if (action === "edit") {
    // Pass the entry to the edit dialog and open it
    editDialogMode.value = "edit";
    editDialogEntry.value = entry;
    editDialogOpen.value = true;
  } else if (action === "duplicate") {
    // Pass the source entry to the add dialog in duplicate mode (no prefill needed)
    addDialogMode.value = "duplicate";
    addDialogEntry.value = entry;
    addDialogPrefill.value = null;
    addDialogOpen.value = true;
  } else if (action === "delete") {
    try {
      await deleteEntry(entry.id);
      await refreshCalendar();
    } catch (err) {
      // Error snackbar is handled by the composable's deleteEntry
    }
  }
}

/**
 * Transitions from the info dialog to the edit dialog for the currently selected event.
 * Closes the info dialog first so both dialogs are not open simultaneously.
 */
function infoToEdit() {
  infoDialogOpen.value = false;
  onEventAction("edit", selectedEvent.value);
}

/**
 * Transitions from the info dialog to the duplicate dialog for the currently selected event.
 * Closes the info dialog first so both dialogs are not open simultaneously.
 */
function infoToDuplicate() {
  infoDialogOpen.value = false;
  onEventAction("duplicate", selectedEvent.value);
}

/**
 * Triggers a delete action for the currently selected event from within the info dialog.
 * Closes the info dialog first, then delegates to onEventAction("delete").
 */
function infoToDelete() {
  infoDialogOpen.value = false;
  onEventAction("delete", selectedEvent.value);
}

/**
 * Fetches a signed download URL for the given asset and displays it in the screenshot viewer dialog.
 * Closes the dialog silently if the API call fails (avoids showing an empty broken dialog).
 */
async function viewScreenshot(assetId) {
  screenshotDialog.value = true;
  screenshotLoading.value = true;
  screenshotUrl.value = null;
  try {
    const res = await api.assetsApi.get(`/api/assets/${assetId}/download`);
    screenshotUrl.value = res.data.url; // Signed URL with short expiry
  } catch {
    screenshotDialog.value = false; // Don't leave an empty dialog open on error
  } finally {
    screenshotLoading.value = false;
  }
}

/**
 * Exports the current schedule as a PDF using the lazy-loaded PDF utility.
 * The utility is imported dynamically to keep the initial bundle size small.
 * Sets exporting=true during the operation to disable the export button.
 */
async function exportPdf() {
  exporting.value = true;
  try {
    const { generateReviewCalendarPdf } = await import("@/utils/reviewCalendarPdfReport.js");
    generateReviewCalendarPdf(scheduleEntries.value, selectedMonth.value, usersMap.value);
  } catch (err) {
    console.error("Failed to export PDF:", err);
    snackbar.value = { show: true, message: "Failed to export PDF", color: "error" };
  } finally {
    exporting.value = false;
  }
}

/**
 * Re-fetches the schedule from the backend and bumps calendarKey to force
 * the v-calendar to fully re-render with the updated events list.
 * Called after any CRUD operation (save, delete) in the dialogs.
 */
async function refreshCalendar() {
  await fetchSchedule();
  calendarKey.value++; // Force v-calendar re-render
}

/**
 * Loads ancillary data needed to display the calendar correctly.
 * Currently: fetches all users from the auth service and builds usersMap
 * (userId → { name, email }) so event chips and dialogs can show user names.
 */
async function loadSupportData() {
  try {
    const usersRes = await api.authApi.get("/api/admin/users");
    const users = usersRes.data || [];
    const map = new Map();
    for (const u of users) {
      // Prefer first + last name; fall back to email if name fields are empty
      const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
      map.set(u.id, { name: name || u.email, email: u.email });
    }
    usersMap.value = map;
  } catch (err) {
    console.error("Failed to load support data:", err);
  }
}

// Register app bar action buttons reactively via watchEffect so that any reactive
// state referenced inside (e.g., scheduleLoading) would automatically update the
// buttons if needed in the future.
watchEffect(() => {
  setActions([
    {
      // Opens the filter dialog to filter calendar events
      icon: "mdi-filter-variant",
      tooltip: "Filters",
      onClick: () => { showFilters.value = true; },
      badge: activeFilterCount.value > 0 ? { content: activeFilterCount.value, color: "warning" } : undefined,
    },
    // Refresh button is intentionally disabled — navigation already triggers a fetch.
    // {
    //   icon: "mdi-refresh",
    //   tooltip: "Refresh",
    //   onClick: refresh,
    //   loading: scheduleLoading.value,
    // },
    {
      // Opens the add dialog with no pre-fill (user must choose dates manually)
      icon: "mdi-plus",
      tooltip: "Add Assignment",
      onClick: () => {
        addDialogMode.value = "add";
        addDialogEntry.value = null;
        addDialogPrefill.value = null;
        addDialogOpen.value = true;
      },
    },
  ]);
});

// Clean up app bar actions and the PDF export ref when leaving this page.
onBeforeUnmount(() => {
  clearActions();
  exportCalendarPdf.value = null; // Remove reference so the PDF trigger no longer points here
});

onMounted(async () => {
  // Register exportPdf so the dot menu's "Export PDF" option can call it
  exportCalendarPdf.value = exportPdf;
  // Load users, review costs config, and schedule in parallel
  await Promise.all([loadSupportData(), loadReviewConfig()]);
  await refreshCalendar();   // Fetch schedule for the current month
});
</script>

<style scoped>
.fill-height {
  height: 100%;
  min-height: calc(100vh - 64px);
}

:deep(.v-calendar) {
  height: 100%;
  min-height: 500px;
}

:deep(.v-calendar-weekly__day) {
  min-height: 80px;
  margin-right: 0 !important;
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  position: relative;
}

:deep(.v-calendar-weekly__head-weekday) {
  margin-right: 0 !important;
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
}

/* Outside (non-current week) days: slightly lighter */
:deep(.v-calendar-weekly__day.v-outside),
:deep(.v-calendar-weekly__head-weekday.v-outside) {
  background-color: rgba(var(--v-border-color), 0.04) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Weekend day background via slot class */
.weekend-day {
  background-color: rgba(var(--v-border-color), 0.06);
  z-index: 0;
}

.border-t {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.day-click-target {
  position: absolute;
  inset: 0;
  cursor: pointer;
  z-index: 0;
}

.day-click-target.past-day {
  cursor: default;
}


:deep(.v-calendar-weekly__day.v-present) {
  background-color: rgba(25, 118, 210, 0.35) !important;
}

:deep(.v-calendar-weekly__day.v-present .v-icon-btn--active) {
  background-color: transparent !important;
  color: inherit !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

:deep(.v-calendar-weekly__day.v-present .v-icon-btn--active::before),
:deep(.v-calendar-weekly__day.v-present .v-icon-btn--active::after) {
  display: none !important;
}


.calendar-event-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1px 4px;
  cursor: pointer;
  overflow: hidden;
  color: #fff;
}

/* Sticky cost badge at the bottom of each day cell */
.day-cost-badge {
  position: absolute;
  bottom: 2px;
  right: 4px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.55);
  background: rgba(var(--v-theme-surface-variant), 0.6);
  padding: 1px 5px;
  border-radius: 4px;
  z-index: 2;
  pointer-events: none;
}

</style>
